import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ReferenceArea,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { cn } from "@/lib/utils";
import { PAGE_TITLE_BAR_APP_TITLE_CLASS } from "@/components/ui/PageTitleBar";
import { formatEurThousands, formatPipelineCompactEur } from "@/lib/format-pipeline-currency";
import {
  getDefaultDashboardFilters,
  useActiveDeals,
  useConversionRate,
  useDashboardHeatmap,
  useDashboardSubpipelines,
  useDealKpis90d,
  useDealStageMovementPanel,
  useDealsCreatedDaily,
  useLeadSources,
  useMonthlyTrend,
  usePipelineStages,
  type DashboardQueryFilters,
} from "@/hooks/useDashboardData";
import { DashboardSkHeatmap } from "@/components/dashboard/DashboardSkHeatmap";
import { DashboardFiltersBar } from "@/components/dashboard/DashboardFiltersBar";
import type {
  CrmDashboardHeatmapSubpipelineMeta,
  CrmDashboardStageMovementCallerSplit,
  CrmDashboardStageMovementSummary,
  DashboardConversionPeriod,
} from "@shared/api";
import type { TFunction } from "i18next";

/** Default subpipeline: garage doors (SK „Garázové brány”, HU garázs, EN garage). */
function defaultGarageSubpipelineId(subs: CrmDashboardHeatmapSubpipelineMeta[]): string | null {
  for (const p of subs) {
    const sk = (p.labelSk || "").toLowerCase();
    if (sk.includes("garázové") && sk.includes("brány")) return p.pipelineId;
    if (sk.includes("garazove") && sk.includes("brany")) return p.pipelineId;
  }
  for (const p of subs) {
    const hu = (p.labelHu || "").toLowerCase();
    if (hu.includes("garáz") && (hu.includes("kapu") || hu.includes("brán"))) return p.pipelineId;
  }
  for (const p of subs) {
    const en = (p.label || "").toLowerCase();
    if (en.includes("garage") && (en.includes("door") || en.includes("gate"))) return p.pipelineId;
  }
  return null;
}

function leadSourceColor(source: string): string {
  const s = source.toLowerCase();
  if (s.includes("facebook")) return "hsl(221 83% 53%)";
  if (s.includes("refer") || s.includes("ajánl") || s.includes("odporúč")) return "hsl(142 71% 45%)";
  if (s.includes("web") || s.includes("site") || s.includes("oldal")) return "hsl(38 92% 50%)";
  return "hsl(220 9% 46%)";
}

function pipelineBarFill(index: number, total: number): string {
  if (total <= 1) return "hsl(214 88% 45%)";
  const t = index / Math.max(1, total - 1);
  const lightness = 78 - t * 43;
  return `hsl(214 85% ${lightness}%)`;
}

function conversionBadgeClass(rate: number | null): string {
  if (rate === null || Number.isNaN(rate)) return "bg-muted text-muted-foreground";
  if (rate >= 40) return "bg-emerald-600 text-white hover:bg-emerald-600";
  if (rate >= 20) return "bg-amber-500 text-amber-950 hover:bg-amber-500";
  return "bg-red-600 text-white hover:bg-red-600";
}

function WidgetError({ message }: { message: string }) {
  return (
    <div className="rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive">
      {message}
    </div>
  );
}

function StageMovementPosNegPair({
  positive,
  negative,
  compact,
}: {
  positive: number;
  negative: number;
  compact?: boolean;
}) {
  return (
    <div
      className={
        compact
          ? "text-sm font-semibold tabular-nums tracking-tight"
          : "text-base font-semibold tabular-nums tracking-tight sm:text-lg"
      }
    >
      <span className="text-emerald-600">{positive}</span>
      <span className="mx-1.5 font-normal text-muted-foreground">/</span>
      <span className="text-red-600">{negative}</span>
    </div>
  );
}

function sumCallerRows(rows: Array<{ positive: number; negative: number }>) {
  return rows.reduce(
    (acc, r) => ({ positive: acc.positive + r.positive, negative: acc.negative + r.negative }),
    { positive: 0, negative: 0 },
  );
}

function StageMovementSummaryBreakdownSection({
  caption,
  subRows,
  totalLabel,
  totalPositive,
  totalNegative,
}: {
  caption: string;
  subRows: Array<{ key: string; label: string; positive: number; negative: number }>;
  totalLabel: string;
  totalPositive: number;
  totalNegative: number;
}) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground mb-2">{caption}</div>
      <ul className="space-y-2 pl-0.5">
        {subRows.map((row) => (
          <li key={row.key} className="flex items-baseline justify-between gap-3">
            <span className="text-xs text-muted-foreground shrink-0">{row.label}</span>
            <StageMovementPosNegPair positive={row.positive} negative={row.negative} compact />
          </li>
        ))}
        <li className="flex items-baseline justify-between gap-3 border-t border-border/50 pt-2">
          <span className="text-xs font-medium text-foreground">{totalLabel}</span>
          <StageMovementPosNegPair positive={totalPositive} negative={totalNegative} compact />
        </li>
      </ul>
    </div>
  );
}

function StageMovementSummaryAside({
  summary: s,
  t,
}: {
  summary: CrmDashboardStageMovementSummary;
  t: TFunction;
}) {
  const callers = s.callerSplits ?? [];
  const totalLabel = t("dashboard.stageSummaryTotalCaption");
  const callDaySubRows = callers.map((c: CrmDashboardStageMovementCallerSplit) => ({
    key: c.key,
    label: c.label,
    positive: c.positiveMovesOnCallDays,
    negative: c.negativeMovesOnCallDays,
  }));
  const otherDaySubRows = callers.map((c: CrmDashboardStageMovementCallerSplit) => ({
    key: c.key,
    label: c.label,
    positive: c.positiveMovesOnOtherDays,
    negative: c.negativeMovesOnOtherDays,
  }));
  const callDayTotal = sumCallerRows(callDaySubRows);
  const otherDayTotal = sumCallerRows(otherDaySubRows);

  return (
    <>
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        {t("dashboard.stageSummaryHeading")}
      </h3>
      <div className="space-y-4">
        <StageMovementSummaryBreakdownSection
          caption={t("dashboard.stageSummaryCallDaysCaption")}
          subRows={callDaySubRows}
          totalLabel={totalLabel}
          totalPositive={callDayTotal.positive}
          totalNegative={callDayTotal.negative}
        />
        <StageMovementSummaryBreakdownSection
          caption={t("dashboard.stageSummaryOtherDaysCaption")}
          subRows={otherDaySubRows}
          totalLabel={totalLabel}
          totalPositive={otherDayTotal.positive}
          totalNegative={otherDayTotal.negative}
        />
        <p className="text-[11px] leading-snug text-muted-foreground">{t("dashboard.stageSummaryTotalHint")}</p>
        <div className="border-t border-border/60 pt-3">
          <div className="text-xs font-medium text-muted-foreground mb-2">{t("dashboard.stageSummaryMoreCaption")}</div>
          <dl className="space-y-1.5 text-sm">
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{t("dashboard.stageSummaryAvgPositivePerDay")}</dt>
              <dd className="tabular-nums font-medium">{s.avgPositiveMovesPerDay}</dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{t("dashboard.stageSummaryPositiveOnCallDaysPct")}</dt>
              <dd className="tabular-nums font-medium">
                {s.positiveOnCallDaysSharePct === null
                  ? t("dashboard.stageSummaryPositiveOnCallDaysPctEmDash")
                  : t("dashboard.stageSummaryPositiveOnCallDaysPctValue", { pct: s.positiveOnCallDaysSharePct })}
              </dd>
            </div>
            <div className="flex justify-between gap-3">
              <dt className="text-muted-foreground">{t("dashboard.stageSummaryNewDealsFiltered")}</dt>
              <dd className="tabular-nums font-medium">{s.newDealsInFilterPeriod}</dd>
            </div>
          </dl>
        </div>
      </div>
      {s.totalPositiveMoves === 0 && s.daysInRange > 0 ? (
        <p className="text-xs text-muted-foreground">{t("dashboard.stageSummaryNoPositiveHint")}</p>
      ) : null}
      <p className="border-t border-border/60 pt-3 text-xs leading-relaxed text-muted-foreground">
        {t("dashboard.stageSummaryFootnoteMixedTimezone")}
      </p>
    </>
  );
}

export default function DashboardPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;
  const [conversionPeriod, setConversionPeriod] = useState<DashboardConversionPeriod>("month");
  const [filters, setFilters] = useState<DashboardQueryFilters>(() => getDefaultDashboardFilters());
  const garageDefaultAttemptedRef = useRef(false);

  const onFiltersChange = useCallback((next: DashboardQueryFilters) => {
    setFilters(next);
  }, []);

  const subpipelinesQ = useDashboardSubpipelines(locale, filters);

  useEffect(() => {
    if (garageDefaultAttemptedRef.current) return;
    const subs = subpipelinesQ.data;
    if (!subs?.length) return;
    garageDefaultAttemptedRef.current = true;
    const pid = defaultGarageSubpipelineId(subs);
    if (!pid) return;
    setFilters((prev) => (prev.pipelineId ? prev : { ...prev, pipelineId: pid }));
  }, [subpipelinesQ.data]);
  const activeQ = useActiveDeals(locale, filters);
  const pipelineQ = usePipelineStages(locale, filters);
  const trendQ = useMonthlyTrend(locale, filters, 6);
  const sourcesQ = useLeadSources(locale, filters);
  const conversionQ = useConversionRate(locale, filters, conversionPeriod);
  const kpisQ = useDealKpis90d(locale, filters);
  const heatmapQ = useDashboardHeatmap(filters);
  const dailyDealsQ = useDealsCreatedDaily(locale, filters);
  const stageMovementPanelQ = useDealStageMovementPanel(locale, filters);

  const filtersActive = Boolean(filters.pipelineId || filters.dateFrom || filters.dateTo);
  const dashboardDateRangeActive = Boolean(filters.dateFrom && filters.dateTo);

  const pipelineChartData = useMemo(() => {
    const rows = pipelineQ.data ?? [];
    const n = rows.length;
    return rows.map((s, i) => ({
      stageId: s.stageId,
      name: locale.startsWith("hu") ? s.labelHu : s.label,
      count: s.count,
      fill: pipelineBarFill(i, n),
    }));
  }, [pipelineQ.data, locale]);

  const donutData = useMemo(() => {
    return (sourcesQ.data ?? []).map((row) => ({
      name: row.source,
      value: row.count,
      fill: leadSourceColor(row.source),
      pct: row.percentage,
    }));
  }, [sourcesQ.data]);

  return (
    <div className="container mx-auto max-w-7xl space-y-6 p-4 md:p-6">
      <div>
        <h1 className={cn(PAGE_TITLE_BAR_APP_TITLE_CLASS, "!pl-0 tracking-tight")}>
          {t("dashboard.title")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">{t("dashboard.subtitle")}</p>
      </div>

      <DashboardFiltersBar filters={filters} onChange={onFiltersChange} subpipelines={subpipelinesQ.data ?? []} />

      {/* Top metric cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("dashboard.cardActiveDeals")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeQ.isLoading ? (
              <Skeleton className="h-9 w-20" />
            ) : activeQ.isError ? (
              <WidgetError message={t("dashboard.loadError")} />
            ) : (
              <div className="text-3xl font-bold tabular-nums">{activeQ.data?.activeCount ?? 0}</div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("dashboard.cardPipelineValue")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {activeQ.isLoading ? (
              <Skeleton className="h-9 w-28" />
            ) : activeQ.isError ? (
              <WidgetError message={t("dashboard.loadError")} />
            ) : (
              <div className="text-3xl font-bold tabular-nums">
                {formatPipelineCompactEur(activeQ.data?.pipelineValue ?? 0)}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("dashboard.cardConversion")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {kpisQ.isLoading ? (
              <Skeleton className="h-9 w-16" />
            ) : kpisQ.isError ? (
              <WidgetError message={t("dashboard.loadError")} />
            ) : (
              <>
                <div className="text-3xl font-bold tabular-nums">
                  {kpisQ.data?.conversionPct != null ? `${kpisQ.data.conversionPct}%` : "—"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {filtersActive ? t("dashboard.cardConversionHintFiltered") : t("dashboard.cardConversionHint")}
                </p>
              </>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              {t("dashboard.cardAvgCycle")}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {kpisQ.isLoading ? (
              <Skeleton className="h-9 w-20" />
            ) : kpisQ.isError ? (
              <WidgetError message={t("dashboard.loadError")} />
            ) : (
              <>
                <div className="text-3xl font-bold tabular-nums">
                  {kpisQ.data?.avgCycleDays != null ? `${kpisQ.data.avgCycleDays} ${t("dashboard.days")}` : "—"}
                </div>
                <p className="text-xs text-muted-foreground mt-1">
                  {filtersActive ? t("dashboard.cardAvgCycleHintFiltered") : t("dashboard.cardAvgCycleHint")}
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <DashboardSkHeatmap
        data={heatmapQ.data}
        isLoading={heatmapQ.isLoading}
        isError={heatmapQ.isError}
        filtersActive={filtersActive}
      />

      {/* Pipeline + lead sources */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        <Card className="xl:col-span-3">
          <CardHeader>
            <CardTitle className="text-base">{t("dashboard.pipelineTitle")}</CardTitle>
          </CardHeader>
          <CardContent className="h-80 min-h-[280px]">
            {pipelineQ.isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : pipelineQ.isError ? (
              <WidgetError message={t("dashboard.loadError")} />
            ) : pipelineChartData.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("dashboard.noData")}</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart layout="vertical" data={pipelineChartData} margin={{ left: 8, right: 16, top: 8, bottom: 8 }}>
                  <XAxis type="number" allowDecimals={false} />
                  <YAxis type="category" dataKey="name" width={130} tick={{ fontSize: 11 }} />
                  <Tooltip formatter={(v: number) => [v, t("dashboard.deals")]} />
                  <Bar dataKey="count" radius={[0, 4, 4, 0]} name={t("dashboard.deals")}>
                    {pipelineChartData.map((entry, i) => (
                      <Cell key={entry.stageId} fill={entry.fill ?? pipelineBarFill(i, pipelineChartData.length)} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">{t("dashboard.leadSourcesTitle")}</CardTitle>
          </CardHeader>
          <CardContent>
            {sourcesQ.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : sourcesQ.isError ? (
              <WidgetError message={t("dashboard.loadError")} />
            ) : donutData.length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("dashboard.noData")}</p>
            ) : (
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
                <div className="h-56 w-full min-w-0 sm:w-1/2">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={donutData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        innerRadius={52}
                        outerRadius={80}
                        paddingAngle={1}
                      >
                        {donutData.map((d) => (
                          <Cell key={d.name} fill={d.fill} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(value: number, _name: string, item: { payload?: { pct?: number } }) => [
                          `${value} (${item?.payload?.pct ?? 0}%)`,
                          t("dashboard.deals"),
                        ]}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <ul className="flex-1 space-y-2 text-sm">
                  {donutData.map((row) => (
                    <li key={row.name} className="flex items-center justify-between gap-2">
                      <span className="flex items-center gap-2 min-w-0">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: row.fill }} />
                        <span className="truncate">{row.name}</span>
                      </span>
                      <span className="shrink-0 text-muted-foreground tabular-nums">{row.pct}%</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("dashboard.dailyNewDealsTitle")}</CardTitle>
          <p className="text-xs text-muted-foreground pt-1">
            {dashboardDateRangeActive
              ? t("dashboard.dailyNewDealsHintFiltered")
              : t("dashboard.dailyNewDealsHintDefault")}
          </p>
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-4 rounded-sm bg-[hsl(221_83%_53%)]" />
              {t("dashboard.legendDealSourceFacebook")}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-4 rounded-sm bg-[hsl(38_92%_50%)]" />
              {t("dashboard.legendDealSourceConfigurator")}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-4 rounded-sm bg-[hsl(220_9%_46%)]" />
              {t("dashboard.legendDealSourceManual")}
            </span>
          </div>
        </CardHeader>
        <CardContent className="h-80 min-h-[280px]">
          {dailyDealsQ.isLoading ? (
            <Skeleton className="h-full w-full" />
          ) : dailyDealsQ.isError ? (
            <WidgetError message={t("dashboard.loadError")} />
          ) : (dailyDealsQ.data ?? []).length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("dashboard.noData")}</p>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={dailyDealsQ.data ?? []} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                <XAxis dataKey="dateLabel" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} width={40} tick={{ fontSize: 10 }} />
                <Tooltip
                  formatter={(value: number, name: string) => [
                    value,
                    name === "facebook"
                      ? t("dashboard.legendDealSourceFacebook")
                      : name === "configurator"
                        ? t("dashboard.legendDealSourceConfigurator")
                        : t("dashboard.legendDealSourceManual"),
                  ]}
                />
                <Bar
                  dataKey="facebook"
                  stackId="daily"
                  fill="hsl(221 83% 53%)"
                  name="facebook"
                  radius={[0, 0, 0, 0]}
                />
                <Bar dataKey="configurator" stackId="daily" fill="hsl(38 92% 50%)" name="configurator" />
                <Bar
                  dataKey="manual"
                  stackId="daily"
                  fill="hsl(220 9% 46%)"
                  name="manual"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">{t("dashboard.stageMovementTitle")}</CardTitle>
          <p className="text-xs text-muted-foreground pt-1">{t("dashboard.stageMovementHint")}</p>
          <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2">
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-4 rounded-sm bg-emerald-600" />
              {t("dashboard.legendStagePositive")}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-4 rounded-sm bg-red-600" />
              {t("dashboard.legendStageNegative")}
            </span>
            <span className="flex items-center gap-1.5">
              <span className="inline-block h-2 w-4 rounded-sm bg-primary/25 ring-1 ring-primary/30" />
              {t("dashboard.legendHighCallDay")}
            </span>
          </div>
        </CardHeader>
        <CardContent className="grid grid-cols-1 gap-4 lg:grid-cols-[1fr_minmax(220px,300px)] lg:items-start">
          <div className="flex min-w-0 flex-col">
            <div className="h-72 min-h-[220px] w-full shrink-0">
              {stageMovementPanelQ.isLoading ? (
                <Skeleton className="h-full w-full" />
              ) : stageMovementPanelQ.isError ? (
                <WidgetError message={t("dashboard.loadError")} />
              ) : (stageMovementPanelQ.data?.rows ?? []).length === 0 ? (
                <p className="text-sm text-muted-foreground">{t("dashboard.noData")}</p>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={stageMovementPanelQ.data?.rows ?? []}
                    margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                  >
                    {(stageMovementPanelQ.data?.rows ?? [])
                      .filter((d) => d.highCallVolume)
                      .map((d) => (
                        <ReferenceArea
                          key={`call-band-${d.dateKey}`}
                          x1={d.dateKey}
                          x2={d.dateKey}
                          strokeOpacity={0}
                          fill="hsl(214 88% 56% / 0.16)"
                          ifOverflow="extendDomain"
                        />
                      ))}
                    <CartesianGrid strokeDasharray="3 3" vertical={false} />
                    <XAxis dataKey="dateKey" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                    <YAxis allowDecimals={false} width={36} tick={{ fontSize: 10 }} />
                    <Tooltip
                      content={({ active, payload }) => {
                        if (!active || !payload?.length) return null;
                        const row = payload[0].payload as {
                          dateKey: string;
                          positive: number;
                          negative: number;
                          callsTeam: number;
                          highCallVolume: boolean;
                        };
                        return (
                          <div className="rounded-md border bg-popover px-2 py-1.5 text-xs shadow-md">
                            <div className="font-medium tabular-nums">{row.dateKey}</div>
                            {row.highCallVolume ? (
                              <div className="text-muted-foreground mt-0.5">{t("dashboard.legendHighCallDay")}</div>
                            ) : null}
                            <div className="mt-1">
                              {t("dashboard.legendStagePositive")}:{" "}
                              <span className="font-medium tabular-nums">{row.positive}</span>
                            </div>
                            <div>
                              {t("dashboard.legendStageNegative")}:{" "}
                              <span className="font-medium tabular-nums">{row.negative}</span>
                            </div>
                            <div className="text-muted-foreground mt-1">
                              {t("dashboard.tooltipCallsTeam", { count: row.callsTeam })}
                            </div>
                          </div>
                        );
                      }}
                    />
                    <Bar dataKey="positive" fill="hsl(142 71% 40%)" name="positive" radius={[2, 2, 0, 0]} />
                    <Bar dataKey="negative" fill="hsl(0 72% 51%)" name="negative" radius={[2, 2, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
            <p className="mt-3 border-t border-border/60 pt-3 text-xs leading-relaxed text-muted-foreground">
              {t("dashboard.stageMovementPositiveFootnote")}
            </p>
          </div>

          <aside className="space-y-3 lg:border-l lg:border-border/60 lg:pl-4">
            {stageMovementPanelQ.isLoading ? (
              <Skeleton className="h-44 w-full" />
            ) : stageMovementPanelQ.isError ? null : stageMovementPanelQ.data ? (
              <StageMovementSummaryAside summary={stageMovementPanelQ.data.summary} t={t} />
            ) : null}
          </aside>
        </CardContent>
      </Card>

      {/* Monthly trend + conversion */}
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-5">
        <Card className="xl:col-span-3">
          <CardHeader>
            <CardTitle className="text-base">{t("dashboard.monthlyTrendTitle")}</CardTitle>
            <div className="flex flex-wrap gap-4 text-xs text-muted-foreground pt-2">
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-4 rounded-sm bg-sky-400" />
                {t("dashboard.legendOfferValue")}
              </span>
              <span className="flex items-center gap-1.5">
                <span className="inline-block h-2 w-4 rounded-sm bg-blue-800" />
                {t("dashboard.legendWonValue")}
              </span>
            </div>
          </CardHeader>
          <CardContent className="h-80 min-h-[280px]">
            {trendQ.isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : trendQ.isError ? (
              <WidgetError message={t("dashboard.loadError")} />
            ) : (trendQ.data ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">{t("dashboard.noData")}</p>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={trendQ.data ?? []} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
                  <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={(v) => formatEurThousands(Number(v))} width={56} tick={{ fontSize: 10 }} />
                  <Tooltip
                    formatter={(value: number, name: string) => [
                      formatPipelineCompactEur(value),
                      name === "offerValue" ? t("dashboard.legendOfferValue") : t("dashboard.legendWonValue"),
                    ]}
                  />
                  <Bar dataKey="offerValue" fill="hsl(199 89% 58%)" name="offerValue" radius={[4, 4, 0, 0]} />
                  <Bar dataKey="wonValue" fill="hsl(222 47% 31%)" name="wonValue" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>

        <Card className="xl:col-span-2">
          <CardHeader className="space-y-3">
            <CardTitle className="text-base">{t("dashboard.conversionTitle")}</CardTitle>
            <ToggleGroup
              type="single"
              value={conversionPeriod}
              onValueChange={(v) => {
                if (v === "week" || v === "month" || v === "quarter") setConversionPeriod(v);
              }}
              variant="outline"
              size="sm"
              className="justify-start"
            >
              <ToggleGroupItem value="week" aria-label="weeks">
                {t("dashboard.periodWeeks")}
              </ToggleGroupItem>
              <ToggleGroupItem value="month" aria-label="months">
                {t("dashboard.periodMonths")}
              </ToggleGroupItem>
              <ToggleGroupItem value="quarter" aria-label="quarters">
                {t("dashboard.periodQuarters")}
              </ToggleGroupItem>
            </ToggleGroup>
          </CardHeader>
          <CardContent>
            {conversionQ.isLoading ? (
              <Skeleton className="h-64 w-full" />
            ) : conversionQ.isError ? (
              <WidgetError message={t("dashboard.loadError")} />
            ) : (
              <div className="overflow-x-auto max-h-[320px] overflow-y-auto rounded-md border">
                <table className="w-full text-sm">
                  <thead className="sticky top-0 bg-muted/80 backdrop-blur">
                    <tr className="border-b text-left">
                      <th className="p-2 font-medium">{t("dashboard.colPeriod")}</th>
                      <th className="p-2 font-medium text-right">{t("dashboard.colLeads")}</th>
                      <th className="p-2 font-medium text-right">{t("dashboard.colWon")}</th>
                      <th className="p-2 font-medium text-right">{t("dashboard.colLost")}</th>
                      <th className="p-2 font-medium text-right">{t("dashboard.colRate")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(conversionQ.data ?? []).map((row) => (
                      <tr key={row.label} className="border-b last:border-0">
                        <td className="p-2 whitespace-nowrap font-medium">{row.label}</td>
                        <td className="p-2 text-right tabular-nums">{row.leadsIn}</td>
                        <td className="p-2 text-right tabular-nums">{row.won}</td>
                        <td className="p-2 text-right tabular-nums">{row.lost}</td>
                        <td className="p-2 text-right">
                          <Badge className={cn("font-mono tabular-nums", conversionBadgeClass(row.conversionRate))}>
                            {row.conversionRate != null ? `${row.conversionRate}%` : "—"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
